import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Medal, Target, Gift, Calendar, TrendingUp, Crown, Award, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Animation = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leaderboardRes, challengesRes, statsRes] = await Promise.all([
        fetch(`${API}/leaderboard`, { credentials: 'include' }),
        fetch(`${API}/challenges`, { credentials: 'include' }),
        fetch(`${API}/dashboard/commercial`, { credentials: 'include' }),
      ]);
      
      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json();
        setLeaderboard(data);
      }
      
      if (challengesRes.ok) {
        const data = await challengesRes.json();
        setChallenges(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setUserStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  const getPositionIcon = (position) => {
    switch (position) {
      case 1: return <Crown className="w-5 h-5 text-amber-500" />;
      case 2: return <Medal className="w-5 h-5 text-slate-400" />;
      case 3: return <Medal className="w-5 h-5 text-orange-400" />;
      default: return <span className="text-sm font-bold text-slate-500">{position}</span>;
    }
  };

  const getPositionClass = (position) => {
    switch (position) {
      case 1: return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200 dark:from-amber-900/20 dark:to-amber-800/20 dark:border-amber-700';
      case 2: return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200 dark:from-slate-800/50 dark:to-slate-800/30 dark:border-slate-700';
      case 3: return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 dark:from-orange-900/20 dark:to-orange-800/20 dark:border-orange-700';
      default: return 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="animation-title">
          Animation commerciale
        </h1>
        <p className="text-slate-500 mt-1">Classements, challenges et objectifs</p>
      </div>

      <Tabs defaultValue="challenges">
        <TabsList className="bg-slate-100 dark:bg-slate-900 p-1">
          <TabsTrigger value="challenges" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Challenges
          </TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" className="mt-6 space-y-6">
          {challenges.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="w-12 h-12 text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Aucun challenge actif</h3>
                <p className="text-slate-500 text-sm mt-1">Les prochains challenges seront annoncés ici</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {challenges.map((challenge, index) => (
                <Card 
                  key={challenge.challenge_id}
                  className="stat-card border-0 shadow-sm overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`challenge-${challenge.challenge_id}`}
                >
                  <div className="h-2 bg-gradient-to-r from-indigo-500 to-violet-500" />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                        <Target className="w-6 h-6 text-indigo-600" />
                      </div>
                      <Badge className="badge-success">
                        <Star className="w-3 h-3 mr-1" />
                        Actif
                      </Badge>
                    </div>
                    
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">
                      {challenge.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {challenge.description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Jusqu'au {new Date(challenge.end_date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-2 mb-3">
                        <Gift className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-slate-900 dark:text-white">
                          Recompense: {challenge.reward}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Votre progression</span>
                          <span className="font-medium text-slate-900 dark:text-white">
                            {userStats?.monthly_revenue?.toLocaleString('fr-FR') || 0} € / {challenge.target?.toLocaleString('fr-FR') || 0} €
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(100, ((userStats?.monthly_revenue || 0) / (challenge.target || 1)) * 100)} 
                          className="h-3"
                        />
                        <p className="text-xs text-slate-500">
                          {Math.min(100, Math.round(((userStats?.monthly_revenue || 0) / (challenge.target || 1)) * 100))}% de l'objectif atteint
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
